import { Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  AdjustmentType,
  OverrideSource,
  StockOverrideCreateInput,
  StockOverrideRecord,
  StockOverrideUpdateInput,
} from "../../../../types/historicalOverrides";
import {
  datetimeLocalToIso,
  EMPTY_FORM_STATE,
  isoToDatetimeLocal,
  isValidDatetimeLocal,
  OverrideFormState,
  UTC_INPUT_PLACEHOLDER,
} from "./types";

interface OverrideFormProps {
  mode: "create" | "edit";
  initial: StockOverrideRecord | null;
  onCreate: (input: StockOverrideCreateInput) => Promise<void>;
  onUpdate: (id: number, input: StockOverrideUpdateInput) => Promise<void>;
  onDelete: (id: number) => void;
  isDarkMode: boolean;
  conId: number;
}

const ADJUSTMENT_TYPES: AdjustmentType[] = ["SPLIT", "DIVIDEND", "MANUAL"];
const SOURCES: OverrideSource[] = ["IBKR", "EODHD"];

function recordToFormState(record: StockOverrideRecord): OverrideFormState {
  return {
    source: record.source,
    startAtLocal: isoToDatetimeLocal(record.startAtUtc),
    endAtLocal: isoToDatetimeLocal(record.endAtUtc),
    adjustmentType: record.adjustmentType,
    adjustmentFactor: record.adjustmentFactor ?? "",
    adjustmentAbsolute: record.adjustmentAbsolute ?? "",
    reason: record.reason ?? "",
    isActive: record.isActive,
  };
}

interface ValidationResult {
  ok: boolean;
  error: string | null;
}

export function validateForm(state: OverrideFormState): ValidationResult {
  if (!state.startAtLocal) {
    return { ok: false, error: "Start datetime is required." };
  }
  if (!isValidDatetimeLocal(state.startAtLocal)) {
    return { ok: false, error: `Start datetime must match ${UTC_INPUT_PLACEHOLDER}.` };
  }
  if (state.endAtLocal && !isValidDatetimeLocal(state.endAtLocal)) {
    return { ok: false, error: `End datetime must match ${UTC_INPUT_PLACEHOLDER}.` };
  }
  if (state.endAtLocal && state.endAtLocal <= state.startAtLocal) {
    return { ok: false, error: "End datetime must be after start datetime." };
  }
  if (!state.reason.trim()) {
    return { ok: false, error: "Reason is required." };
  }
  const hasFactor = state.adjustmentFactor.trim() !== "";
  const hasAbsolute = state.adjustmentAbsolute.trim() !== "";
  if (hasFactor && hasAbsolute) {
    return {
      ok: false,
      error: "Provide exactly one of adjustment factor or adjustment absolute, not both.",
    };
  }
  if (!hasFactor && !hasAbsolute) {
    return {
      ok: false,
      error: "One of adjustment factor or adjustment absolute is required.",
    };
  }
  return { ok: true, error: null };
}

const OverrideForm: React.FC<OverrideFormProps> = ({
  mode,
  initial,
  onCreate,
  onUpdate,
  onDelete,
  isDarkMode,
  conId,
}) => {
  const [state, setState] = useState<OverrideFormState>(EMPTY_FORM_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === "edit" && initial) {
      setState(recordToFormState(initial));
    } else {
      setState(EMPTY_FORM_STATE);
    }
    setInlineError(null);
  }, [mode, initial]);

  const update = <K extends keyof OverrideFormState>(
    key: K,
    value: OverrideFormState[K]
  ) => {
    setState((s) => ({ ...s, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateForm(state);
    if (!validation.ok) {
      setInlineError(validation.error);
      return;
    }
    setInlineError(null);
    setSubmitting(true);
    try {
      const factor = state.adjustmentFactor.trim();
      const absolute = state.adjustmentAbsolute.trim();
      const startIso = datetimeLocalToIso(state.startAtLocal);
      const endIso = state.endAtLocal ? datetimeLocalToIso(state.endAtLocal) : null;

      if (mode === "create") {
        const input: StockOverrideCreateInput = {
          conId,
          source: state.source,
          startAtUtc: startIso,
          endAtUtc: endIso,
          adjustmentType: state.adjustmentType,
          adjustmentFactor: factor || null,
          adjustmentAbsolute: absolute || null,
          reason: state.reason.trim(),
        };
        await onCreate(input);
      } else if (initial) {
        const input: StockOverrideUpdateInput = {
          startAtUtc: startIso,
          endAtUtc: endIso,
          adjustmentType: state.adjustmentType,
          adjustmentFactor: factor || null,
          adjustmentAbsolute: absolute || null,
          reason: state.reason.trim(),
          isActive: state.isActive,
        };
        await onUpdate(initial.id, input);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const label = `block text-xs font-medium mb-1 ${
    isDarkMode ? "text-gray-300" : "text-gray-700"
  }`;
  const input = `w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 ${
    isDarkMode
      ? "bg-gray-800 border-gray-700 text-gray-100 focus:ring-blue-500"
      : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500"
  }`;

  return (
    <form
      onSubmit={handleSubmit}
      className="h-full flex flex-col overflow-y-auto p-6"
      data-testid="override-form"
    >
      <h3
        className={`text-lg font-semibold mb-4 ${
          isDarkMode ? "text-gray-100" : "text-gray-900"
        }`}
      >
        {mode === "create" ? "Create new override" : `Edit override #${initial?.id ?? ""}`}
      </h3>

      <div className="grid grid-cols-2 gap-4">
        {mode === "create" && (
          <div className="col-span-2">
            <label className={label} htmlFor="ov-source">Source</label>
            <select
              id="ov-source"
              className={input}
              value={state.source}
              onChange={(e) => update("source", e.target.value as OverrideSource)}
            >
              {SOURCES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className={label} htmlFor="ov-start">Start (UTC)</label>
          <input
            id="ov-start"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            placeholder={UTC_INPUT_PLACEHOLDER}
            className={input}
            value={state.startAtLocal}
            onChange={(e) => update("startAtLocal", e.target.value)}
          />
        </div>

        <div>
          <label className={label} htmlFor="ov-end">End (UTC, optional)</label>
          <input
            id="ov-end"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            placeholder={UTC_INPUT_PLACEHOLDER}
            className={input}
            value={state.endAtLocal}
            onChange={(e) => update("endAtLocal", e.target.value)}
          />
        </div>

        <div className="col-span-2">
          <label className={label} htmlFor="ov-type">Adjustment type</label>
          <select
            id="ov-type"
            className={input}
            value={state.adjustmentType}
            onChange={(e) => update("adjustmentType", e.target.value as AdjustmentType)}
          >
            {ADJUSTMENT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={label} htmlFor="ov-factor">Adjustment factor</label>
          <input
            id="ov-factor"
            type="text"
            inputMode="decimal"
            placeholder="e.g. 0.5"
            className={input}
            value={state.adjustmentFactor}
            onChange={(e) => update("adjustmentFactor", e.target.value)}
          />
        </div>

        <div>
          <label className={label} htmlFor="ov-absolute">Adjustment absolute</label>
          <input
            id="ov-absolute"
            type="text"
            inputMode="decimal"
            placeholder="e.g. 1.25"
            className={input}
            value={state.adjustmentAbsolute}
            onChange={(e) => update("adjustmentAbsolute", e.target.value)}
          />
        </div>

        <div className="col-span-2">
          <label className={label} htmlFor="ov-reason">Reason</label>
          <textarea
            id="ov-reason"
            className={`${input} min-h-[80px]`}
            value={state.reason}
            onChange={(e) => update("reason", e.target.value)}
          />
        </div>

        {mode === "edit" && (
          <div className="col-span-2 flex items-center gap-2">
            <input
              id="ov-active"
              type="checkbox"
              checked={state.isActive}
              onChange={(e) => update("isActive", e.target.checked)}
            />
            <label
              htmlFor="ov-active"
              className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              Active
            </label>
          </div>
        )}
      </div>

      {inlineError && (
        <div
          role="alert"
          className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300"
        >
          {inlineError}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between gap-3">
        {mode === "edit" && initial ? (
          <button
            type="button"
            onClick={() => onDelete(initial.id)}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        ) : (
          <span />
        )}
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50"
        >
          {submitting
            ? "Saving..."
            : mode === "create"
              ? "Create override"
              : "Update override"}
        </button>
      </div>
    </form>
  );
};

export default OverrideForm;
